import React, { useEffect, useRef } from 'react';

/*
const RecordingWaveform = ({ stream, isRecording }) => {
*/
const RecordingWaveform = ({ stream, isRecording, isMuted }) => {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Layout parameters for waves
  const barWidth = 4;
  const barGap = 3;

  // Array of heights history
  const barHeightsRef = useRef([]);
  const scrollOffsetRef = useRef(0);
  const lastTimeRef = useRef(performance.now());

  // Setup Web Audio API when recording/stream is active
  useEffect(() => {
    if (isRecording && stream) {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioContextClass();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioCtx;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;
        sourceRef.current = source;
      } catch (err) {
        console.error('Failed to setup AudioContext for visualizer:', err);
      }
    }

    return () => {
      // Clean up resources on unmount or stream change
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
      sourceRef.current = null;
    };
  }, [isRecording, stream]);

  // Canvas drawing and scrolling animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    // Calculate how many bars fit in the width
    const stepWidth = barWidth + barGap;
    const barsCount = Math.ceil(width / stepWidth);

    // Initialize/Adjust history array size to match the current width
    if (barHeightsRef.current.length < barsCount) {
      const pad = Array(barsCount - barHeightsRef.current.length).fill(6);
      barHeightsRef.current = [...pad, ...barHeightsRef.current];
    } else if (barHeightsRef.current.length > barsCount) {
      barHeightsRef.current = barHeightsRef.current.slice(-barsCount);
    }

    const draw = (timestamp) => {
      if (!canvasRef.current) return;
      ctx.clearRect(0, 0, width, height);

      // Scroll speed based on delta time to keep it perfectly constant across screens
      const dt = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const scrollSpeed = 0.08; // pixels per ms
      if (isRecording) {
        scrollOffsetRef.current += scrollSpeed * dt;
      }

      // When scroll offset passes one complete bar step, shift the heights array
      while (scrollOffsetRef.current >= stepWidth) {
        scrollOffsetRef.current -= stepWidth;

        // Retrieve current audio amplitude
        let volume = 0;
        /*
        if (analyserRef.current && dataArrayRef.current && isRecording) {
        */
        if (analyserRef.current && dataArrayRef.current && isRecording && !isMutedRef.current) {
          analyserRef.current.getByteFrequencyData(dataArrayRef.current);
          let sum = 0;
          for (let i = 0; i < dataArrayRef.current.length; i++) {
            sum += dataArrayRef.current[i];
          }
          volume = sum / dataArrayRef.current.length;
        }

        let barHeight = 6;
        if (isRecording) {
          if (volume > 4) {
            // Map 0-255 frequency average to canvas height
            barHeight = 6 + (volume / 120) * (height - 12);
          } else {
            // Idle traveling wiggles (sine + noise) so waves keep moving
            barHeight = 6 + Math.sin(timestamp / 100) * 3 + Math.random() * 2;
          }
        } else {
          barHeight = 6;
        }

        // Limit the height to match canvas bounds
        barHeight = Math.max(6, Math.min(height - 6, barHeight));

        // Shift heights array left and push new height to the right
        barHeightsRef.current.shift();
        barHeightsRef.current.push(barHeight);
      }

      // Draw the vertical wave bars
      ctx.fillStyle = isRecording ? '#3D5A80' : '#b0b0b0';

      for (let i = 0; i < barsCount; i++) {
        const x = i * stepWidth - scrollOffsetRef.current;
        const barHeight = barHeightsRef.current[i];
        const y = (height - barHeight) / 2;

        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
        } else {
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    lastTimeRef.current = performance.now();
    animationFrameRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording]);

  return (
    <div style={{ width: '100%', height: '100px', paddingLeft: '16px', paddingRight: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default RecordingWaveform;
